import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  GraduationCap, Users, Calendar, MapPin, Briefcase, Mail, Phone,
  Search, Filter, Grid, List, Heart, MessageSquare, Share2, 
  Award, BookOpen, Star, ChevronRight, Globe, Linkedin, Twitter,
  Building, Clock, CheckCircle, Plus, Camera, Edit, UserPlus,
  TrendingUp, DollarSign, HandHeart, CalendarDays, Bell
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

export const AlumniPortal = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("directory");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterYear, setFilterYear] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  
  // Alumni data
  const [alumni, setAlumni] = useState([]);
  const [events, setEvents] = useState([]);
  const [news, setNews] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [donations, setDonations] = useState({ total: 0, goal: 50000, donors: 0 });
  
  // Registration form
  const [registerForm, setRegisterForm] = useState({
    graduationYear: "",
    currentJob: "",
    company: "",
    location: "",
    bio: "",
    linkedin: "",
    phone: "",
    shareContact: true
  });

  useEffect(() => {
    fetchAlumniData();
  }, [token]);

  const fetchAlumniData = async () => {
    setLoading(true);
    try {
      // Fetch alumni directory
      const alumniRes = await apiCall("GET", "/alumni", null, token).catch(() => []);
      setAlumni(alumniRes.length > 0 ? alumniRes : mockAlumni);
      
      // Fetch alumni events
      const eventsRes = await apiCall("GET", "/alumni/events", null, token).catch(() => []);
      setEvents(eventsRes.length > 0 ? eventsRes : mockEvents);
      
      // Fetch alumni news
      const newsRes = await apiCall("GET", "/alumni/news", null, token).catch(() => []);
      setNews(newsRes.length > 0 ? newsRes : mockNews);
      
      // Fetch job postings
      const jobsRes = await apiCall("GET", "/alumni/jobs", null, token).catch(() => []);
      setJobs(jobsRes.length > 0 ? jobsRes : mockJobs);
      
    } catch (err) {
      console.error("Failed to fetch alumni data:", err);
    }
    setLoading(false);
  };

  // Mock data for demo
  const mockAlumni = [
    {
      id: "1",
      name: "Kwame Asante",
      graduationYear: "2018",
      currentJob: "Software Engineer",
      company: "Google Ghana",
      location: "Accra, Ghana",
      bio: "Passionate about technology and education. Proud YCP alumnus!",
      linkedin: "linkedin.com/in/kwameasante",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop",
      verified: true
    },
    {
      id: "2",
      name: "Abena Mensah",
      graduationYear: "2019",
      currentJob: "Medical Doctor",
      company: "Korle Bu Teaching Hospital",
      location: "Accra, Ghana",
      bio: "Healthcare advocate, dedicated to improving lives in Ghana.",
      linkedin: "linkedin.com/in/abenamensah",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&h=150&fit=crop",
      verified: true
    },
    {
      id: "3",
      name: "Kofi Owusu",
      graduationYear: "2017",
      currentJob: "Entrepreneur",
      company: "TechStart Ghana",
      location: "Takoradi, Ghana",
      bio: "Building the future of tech in West Africa. YCP shaped my vision!",
      linkedin: "linkedin.com/in/kofiowusu",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop",
      verified: true
    },
    {
      id: "4",
      name: "Ama Darko",
      graduationYear: "2020",
      currentJob: "Civil Engineer",
      company: "Ghana Highway Authority",
      location: "Kumasi, Ghana",
      bio: "Infrastructure development for a better Ghana.",
      linkedin: "linkedin.com/in/amadarko",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop",
      verified: false
    },
    {
      id: "5",
      name: "Yaw Frimpong",
      graduationYear: "2016",
      currentJob: "Bank Manager",
      company: "GCB Bank",
      location: "Cape Coast, Ghana",
      bio: "Financial expert helping Ghanaians achieve their dreams.",
      linkedin: "linkedin.com/in/yawfrimpong",
      image: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=150&h=150&fit=crop",
      verified: true
    },
    {
      id: "6",
      name: "Efua Sarpong",
      graduationYear: "2021",
      currentJob: "Journalist",
      company: "Joy News",
      location: "Accra, Ghana",
      bio: "Telling stories that matter. YCP taught me to speak up!",
      linkedin: "linkedin.com/in/efuasarpong",
      image: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&h=150&fit=crop",
      verified: true
    }
  ];

  const mockEvents = [
    {
      id: "1",
      title: "Annual Alumni Homecoming 2026",
      date: "2026-03-15",
      time: "10:00 AM",
      location: "YCP School Campus, Takoradi",
      description: "Join us for our annual homecoming celebration! Reconnect with classmates, meet current students, and celebrate our shared heritage.",
      image: "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=400&h=200&fit=crop",
      attendees: 156,
      category: "Reunion"
    },
    {
      id: "2",
      title: "Career Networking Night",
      date: "2026-02-28",
      time: "6:00 PM",
      location: "Movenpick Ambassador Hotel, Accra",
      description: "Connect with fellow alumni across various industries. Great opportunity for mentorship and career growth.",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=200&fit=crop",
      attendees: 89,
      category: "Networking"
    },
    {
      id: "3",
      title: "Alumni Charity Fundraiser",
      date: "2026-04-10",
      time: "7:00 PM",
      location: "Atlantic Beach Resort, Takoradi",
      description: "Annual fundraiser to support scholarships for underprivileged students. Dinner, auction, and entertainment.",
      image: "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?w=400&h=200&fit=crop",
      attendees: 72,
      category: "Fundraiser"
    }
  ];

  const mockNews = [
    {
      id: "1",
      title: "YCP Alumnus Wins National Innovation Award",
      date: "2026-01-20",
      excerpt: "Kofi Owusu (Class of 2017) has been recognized for his groundbreaking work in renewable energy solutions.",
      image: "https://images.unsplash.com/photo-1559136555-9303baea8ebd?w=400&h=200&fit=crop",
      category: "Achievement"
    },
    {
      id: "2",
      title: "Alumni Association Launches Mentorship Program",
      date: "2026-01-15",
      excerpt: "New program connects current students with successful alumni for career guidance and support.",
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=400&h=200&fit=crop",
      category: "Program"
    },
    {
      id: "3",
      title: "Class of 2016 Celebrates 10-Year Reunion",
      date: "2026-01-10",
      excerpt: "Over 50 alumni gathered to celebrate a decade since graduation with fond memories and new connections.",
      image: "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=200&fit=crop",
      category: "Reunion"
    }
  ];

  const mockJobs = [
    {
      id: "1",
      title: "Junior Software Developer",
      company: "TechStart Ghana",
      location: "Accra, Ghana",
      type: "Full-time",
      postedBy: "Kofi Owusu",
      postedDate: "2026-01-25",
      description: "Looking for passionate developers to join our growing team.",
      requirements: ["BSc in Computer Science", "JavaScript/Python", "Team player"]
    },
    {
      id: "2",
      title: "Marketing Associate",
      company: "GCB Bank",
      location: "Cape Coast, Ghana",
      type: "Full-time",
      postedBy: "Yaw Frimpong",
      postedDate: "2026-01-22",
      description: "Join our marketing team to drive brand awareness.",
      requirements: ["Marketing degree", "Creative thinking", "Social media skills"]
    },
    {
      id: "3",
      title: "Teaching Assistant",
      company: "YCP School",
      location: "Takoradi, Ghana",
      type: "Part-time",
      postedBy: "Alumni Office",
      postedDate: "2026-01-20",
      description: "Help current students as a teaching assistant. Great for recent graduates.",
      requirements: ["YCP Alumni", "Good academic record", "Passion for teaching"]
    }
  ];

  const graduationYears = ["all", "2025", "2024", "2023", "2022", "2021", "2020", "2019", "2018", "2017", "2016", "2015"];

  const filteredAlumni = alumni.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         a.company?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         a.currentJob?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear = filterYear === "all" || a.graduationYear === filterYear;
    return matchesSearch && matchesYear;
  });

  const handleRegister = async () => {
    if (!registerForm.graduationYear) {
      toast.error("Please enter your graduation year");
      return;
    }
    
    try {
      await apiCall("POST", "/alumni/register", {
        ...registerForm,
        user_id: user?.id,
        name: user ? `${user.first_name} ${user.last_name}` : "Guest"
      }, token);
      
      toast.success("Successfully registered as alumni!");
      setShowRegisterModal(false);
      fetchAlumniData();
    } catch (err) {
      // For demo, just show success
      toast.success("Registration submitted for review!");
      setShowRegisterModal(false);
    }
  };

  const AlumniCard = ({ alumnus }) => (
    <Card 
      className="bg-white hover:shadow-lg transition-all cursor-pointer group"
      onClick={() => setSelectedAlumni(alumnus)}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <Avatar className="w-16 h-16 border-2 border-[#FF6B00]">
            <AvatarImage src={alumnus.image} alt={alumnus.name} />
            <AvatarFallback className="bg-[#002147] text-white text-lg">
              {alumnus.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[#002147] group-hover:text-[#FF6B00] transition-colors">
                {alumnus.name}
              </h3>
              {alumnus.verified && (
                <CheckCircle size={16} className="text-green-500" />
              )}
            </div>
            <p className="text-sm text-[#FF6B00] font-medium">Class of {alumnus.graduationYear}</p>
            <p className="text-sm text-gray-600 mt-1">{alumnus.currentJob}</p>
            <p className="text-xs text-gray-500">{alumnus.company}</p>
            <div className="flex items-center gap-1 mt-2 text-xs text-gray-400">
              <MapPin size={12} /> {alumnus.location}
            </div>
          </div>
        </div>
        {alumnus.bio && (
          <p className="text-sm text-gray-500 mt-3 line-clamp-2">{alumnus.bio}</p>
        )}
        <div className="flex gap-2 mt-3">
          <Button variant="outline" size="sm" className="flex-1">
            <MessageSquare size={14} className="mr-1" /> Connect
          </Button>
          {alumnus.linkedin && (
            <Button variant="outline" size="sm">
              <Linkedin size={14} />
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const EventCard = ({ event }) => (
    <Card className="bg-white hover:shadow-lg transition-all overflow-hidden">
      <img src={event.image} alt={event.title} className="w-full h-40 object-cover" />
      <CardContent className="p-4">
        <Badge className="bg-[#FF6B00] mb-2">{event.category}</Badge>
        <h3 className="font-bold text-[#002147] mb-2">{event.title}</h3>
        <div className="space-y-1 text-sm text-gray-600">
          <p className="flex items-center gap-2">
            <Calendar size={14} /> {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
          <p className="flex items-center gap-2">
            <Clock size={14} /> {event.time}
          </p>
          <p className="flex items-center gap-2">
            <MapPin size={14} /> {event.location}
          </p>
        </div>
        <p className="text-sm text-gray-500 mt-2 line-clamp-2">{event.description}</p>
        <div className="flex items-center justify-between mt-4">
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Users size={12} /> {event.attendees} attending
          </span>
          <Button size="sm" className="bg-[#FF6B00] hover:bg-[#E65C00]">
            RSVP
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  const JobCard = ({ job }) => (
    <Card className="bg-white hover:shadow-md transition-all">
      <CardContent className="p-4">
        <div className="flex justify-between items-start">
          <div>
            <h3 className="font-bold text-[#002147]">{job.title}</h3>
            <p className="text-sm text-gray-600">{job.company}</p>
          </div>
          <Badge variant="outline">{job.type}</Badge>
        </div>
        <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
          <span className="flex items-center gap-1"><MapPin size={12} /> {job.location}</span>
          <span className="flex items-center gap-1"><Clock size={12} /> {job.postedDate}</span>
        </div>
        <p className="text-sm text-gray-600 mt-2">{job.description}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {job.requirements.map((req, i) => (
            <Badge key={i} variant="secondary" className="text-xs">{req}</Badge>
          ))}
        </div>
        <div className="flex justify-between items-center mt-3 pt-3 border-t">
          <span className="text-xs text-gray-400">Posted by {job.postedBy}</span>
          <Button size="sm" className="bg-[#002147] hover:bg-[#003366]">
            Apply Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="alumni-portal">
      {/* Hero Section */}
      <div className="bg-[#002147] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-[#FF6B00] rounded-full flex items-center justify-center">
                  <GraduationCap size={32} />
                </div>
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold">Alumni Network</h1>
                  <p className="text-white/80">Young Christian Preparatory School / JHS</p>
                </div>
              </div>
              <p className="text-lg text-white/90 max-w-xl">
                Connect with fellow graduates, share opportunities, and stay involved with your alma mater. 
                Once a YCP student, always part of the family!
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                size="lg" 
                className="bg-[#FF6B00] hover:bg-[#E65C00]"
                onClick={() => setShowRegisterModal(true)}
              >
                <UserPlus size={18} className="mr-2" /> Join Alumni Network
              </Button>
              {user && (
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-white text-white hover:bg-white/10"
                  onClick={() => navigate("/messages")}
                >
                  <MessageSquare size={18} className="mr-2" /> Messages
                </Button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{alumni.length}+</p>
              <p className="text-sm text-white/70">Registered Alumni</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{graduationYears.length - 1}</p>
              <p className="text-sm text-white/70">Graduating Classes</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{jobs.length}</p>
              <p className="text-sm text-white/70">Job Opportunities</p>
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold">{events.length}</p>
              <p className="text-sm text-white/70">Upcoming Events</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white mb-6 flex-wrap h-auto p-1">
            <TabsTrigger value="directory" className="flex items-center gap-2">
              <Users size={16} /> Directory
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <CalendarDays size={16} /> Events
            </TabsTrigger>
            <TabsTrigger value="jobs" className="flex items-center gap-2">
              <Briefcase size={16} /> Job Board
            </TabsTrigger>
            <TabsTrigger value="news" className="flex items-center gap-2">
              <Bell size={16} /> News
            </TabsTrigger>
            <TabsTrigger value="donate" className="flex items-center gap-2">
              <HandHeart size={16} /> Give Back
            </TabsTrigger>
          </TabsList>

          {/* Directory Tab */}
          <TabsContent value="directory">
            <Card className="bg-white mb-6">
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      placeholder="Search alumni by name, company, or job..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={filterYear} onValueChange={setFilterYear}>
                    <SelectTrigger className="w-full md:w-48">
                      <Filter size={16} className="mr-2" />
                      <SelectValue placeholder="Graduation Year" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Years</SelectItem>
                      {graduationYears.slice(1).map(year => (
                        <SelectItem key={year} value={year}>Class of {year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex border rounded-lg overflow-hidden">
                    <Button
                      variant={viewMode === "grid" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("grid")}
                      className={viewMode === "grid" ? "bg-[#002147]" : ""}
                    >
                      <Grid size={18} />
                    </Button>
                    <Button
                      variant={viewMode === "list" ? "default" : "ghost"}
                      size="sm"
                      onClick={() => setViewMode("list")}
                      className={viewMode === "list" ? "bg-[#002147]" : ""}
                    >
                      <List size={18} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <p className="text-[#002147] font-medium mb-4">
              Showing {filteredAlumni.length} alumni
            </p>

            <div className={viewMode === "grid" 
              ? "grid md:grid-cols-2 lg:grid-cols-3 gap-6" 
              : "space-y-4"
            }>
              {filteredAlumni.map(alumnus => (
                <AlumniCard key={alumnus.id} alumnus={alumnus} />
              ))}
            </div>

            {filteredAlumni.length === 0 && (
              <div className="text-center py-12">
                <Users className="mx-auto text-gray-400 mb-4" size={48} />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No alumni found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {events.map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-[#002147]">Job Opportunities from Alumni</h2>
              <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                <Plus size={16} className="mr-2" /> Post a Job
              </Button>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {jobs.map(job => (
                <JobCard key={job.id} job={job} />
              ))}
            </div>
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="space-y-6">
              {news.map(item => (
                <Card key={item.id} className="bg-white overflow-hidden">
                  <div className="md:flex">
                    <img src={item.image} alt={item.title} className="w-full md:w-64 h-48 object-cover" />
                    <CardContent className="p-6 flex-1">
                      <Badge className="bg-[#002147] mb-2">{item.category}</Badge>
                      <h3 className="text-xl font-bold text-[#002147] mb-2">{item.title}</h3>
                      <p className="text-gray-600 mb-4">{item.excerpt}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-400">{item.date}</span>
                        <Button variant="outline">Read More <ChevronRight size={16} /></Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Donate Tab */}
          <TabsContent value="donate">
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-white">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <HandHeart className="text-[#FF6B00]" /> Support YCP
                  </CardTitle>
                  <CardDescription>
                    Help us continue providing quality education to future generations
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="mb-6">
                    <div className="flex justify-between mb-2">
                      <span className="font-semibold text-[#002147]">Annual Fundraising Goal</span>
                      <span className="text-[#FF6B00]">${donations.total.toLocaleString()} / ${donations.goal.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-gradient-to-r from-[#FF6B00] to-[#FFA500] h-4 rounded-full transition-all duration-500"
                        style={{ width: `${Math.min((donations.total / donations.goal) * 100, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{donations.donors} donors have contributed</p>
                  </div>

                  <div className="space-y-3">
                    <h4 className="font-semibold text-[#002147]">Select Donation Amount</h4>
                    <div className="grid grid-cols-3 gap-3">
                      {[25, 50, 100, 250, 500, 1000].map(amount => (
                        <Button key={amount} variant="outline" className="hover:bg-[#FF6B00] hover:text-white hover:border-[#FF6B00]">
                          ${amount}
                        </Button>
                      ))}
                    </div>
                    <Input placeholder="Custom amount ($)" type="number" />
                    <Button className="w-full bg-[#FF6B00] hover:bg-[#E65C00]" size="lg">
                      <Heart size={18} className="mr-2" /> Donate Now
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <div className="space-y-6">
                <Card className="bg-white">
                  <CardContent className="p-6">
                    <h4 className="font-bold text-[#002147] mb-4">How Your Donation Helps</h4>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <BookOpen size={20} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Scholarships</p>
                          <p className="text-sm text-gray-500">Support underprivileged students</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Building size={20} className="text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Infrastructure</p>
                          <p className="text-sm text-gray-500">Improve school facilities</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <Award size={20} className="text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-700">Programs</p>
                          <p className="text-sm text-gray-500">Fund extracurricular activities</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-r from-[#002147] to-[#003366] text-white">
                  <CardContent className="p-6">
                    <h4 className="font-bold mb-2">Become a Monthly Donor</h4>
                    <p className="text-white/80 text-sm mb-4">
                      Make a lasting impact with a recurring donation. Monthly donors receive special recognition and updates.
                    </p>
                    <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#002147]">
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Alumni Registration Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="text-[#FF6B00]" /> Join Alumni Network
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Graduation Year *</Label>
                <Select 
                  value={registerForm.graduationYear} 
                  onValueChange={(v) => setRegisterForm({...registerForm, graduationYear: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {graduationYears.slice(1).map(year => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Current Location</Label>
                <Input 
                  value={registerForm.location}
                  onChange={(e) => setRegisterForm({...registerForm, location: e.target.value})}
                  placeholder="e.g., Accra, Ghana"
                />
              </div>
            </div>
            <div>
              <Label>Current Job Title</Label>
              <Input 
                value={registerForm.currentJob}
                onChange={(e) => setRegisterForm({...registerForm, currentJob: e.target.value})}
                placeholder="e.g., Software Engineer"
              />
            </div>
            <div>
              <Label>Company/Organization</Label>
              <Input 
                value={registerForm.company}
                onChange={(e) => setRegisterForm({...registerForm, company: e.target.value})}
                placeholder="e.g., Google Ghana"
              />
            </div>
            <div>
              <Label>Short Bio</Label>
              <Textarea 
                value={registerForm.bio}
                onChange={(e) => setRegisterForm({...registerForm, bio: e.target.value})}
                placeholder="Tell us about yourself..."
                rows={3}
              />
            </div>
            <div>
              <Label>LinkedIn Profile (optional)</Label>
              <Input 
                value={registerForm.linkedin}
                onChange={(e) => setRegisterForm({...registerForm, linkedin: e.target.value})}
                placeholder="linkedin.com/in/yourprofile"
              />
            </div>
            <div>
              <Label>Phone Number (optional)</Label>
              <Input 
                value={registerForm.phone}
                onChange={(e) => setRegisterForm({...registerForm, phone: e.target.value})}
                placeholder="+233 XX XXX XXXX"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegisterModal(false)}>Cancel</Button>
            <Button className="bg-[#FF6B00] hover:bg-[#E65C00]" onClick={handleRegister}>
              Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alumni Detail Modal */}
      {selectedAlumni && (
        <Dialog open={!!selectedAlumni} onOpenChange={() => setSelectedAlumni(null)}>
          <DialogContent className="sm:max-w-lg">
            <div className="text-center">
              <Avatar className="w-24 h-24 mx-auto border-4 border-[#FF6B00]">
                <AvatarImage src={selectedAlumni.image} alt={selectedAlumni.name} />
                <AvatarFallback className="bg-[#002147] text-white text-2xl">
                  {selectedAlumni.name.split(' ').map(n => n[0]).join('')}
                </AvatarFallback>
              </Avatar>
              <h2 className="text-2xl font-bold text-[#002147] mt-4">{selectedAlumni.name}</h2>
              <p className="text-[#FF6B00] font-medium">Class of {selectedAlumni.graduationYear}</p>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Briefcase size={16} className="text-gray-500" />
                <span className="text-gray-600">{selectedAlumni.currentJob} at {selectedAlumni.company}</span>
              </div>
              <div className="flex items-center justify-center gap-2 mt-1">
                <MapPin size={16} className="text-gray-500" />
                <span className="text-gray-500">{selectedAlumni.location}</span>
              </div>
              {selectedAlumni.bio && (
                <p className="text-gray-600 mt-4 text-sm">{selectedAlumni.bio}</p>
              )}
              <div className="flex gap-3 mt-6 justify-center">
                <Button className="bg-[#FF6B00] hover:bg-[#E65C00]">
                  <MessageSquare size={16} className="mr-2" /> Send Message
                </Button>
                {selectedAlumni.linkedin && (
                  <Button variant="outline">
                    <Linkedin size={16} className="mr-2" /> LinkedIn
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AlumniPortal;
